import { ButtonSize, GhostButton } from '@ardoq/button';
import { colors, s16, s8, s64, s4 } from '@ardoq/design-tokens';
import { EditIcon, Icon, IconName, IconSize, SettingsIcon } from '@ardoq/icons';
import { MouseEventHandler, ReactNode } from 'react';
import styled from 'styled-components';

import { ComplaintLevel, Config, TableColumnMappingType } from './types';
import { BadgeDot, BadgeDotSize, StatusType } from '@ardoq/status-ui';
import { WithPopover } from '@ardoq/popovers';
import { Box, FlexBox } from '@ardoq/layout';
import { type WithI18nLocale, t } from '@ardoq/i18n';

export type ColumnParams = {
  title: ReactNode;
  config: Config;
  lastColumn?: boolean;
  isReadyForMapping: boolean;
  hasActiveFilters: boolean;
  onConfirm?: () => void;
  onFilter: () => void;
  onSelectColumn: () => void;
  isHighlighted?: boolean;
  complaint: { level: ComplaintLevel; message: string } | null;
  hideConfig?: boolean;
  advancedLabel?: string;
  confirmLabel?: string;
  clickToConfigureLabel?: string;
};

type ColumnHeaderAgGridParams = ColumnParams & {
  context: WithI18nLocale;
};

export const ColumnHeader = ({
  context,
  title,
  config,
  lastColumn = false,
  isReadyForMapping,
  hasActiveFilters,
  onConfirm,
  onFilter,
  onSelectColumn,
  isHighlighted,
  complaint,
  hideConfig,
  advancedLabel = t(
    context.i18nLocale,
    'integrations:columnHeader.advanced',
    'Advanced'
  ),
  confirmLabel = t(
    context.i18nLocale,
    'integrations:columnHeader.confirm',
    'Confirm'
  ),
  clickToConfigureLabel = t(
    context.i18nLocale,
    'integrations:columnHeader.clickToConfigure',
    'Click to configure'
  ),
}: ColumnHeaderAgGridParams) => {
  if (hideConfig) {
    return <NoConfig>{title}</NoConfig>;
  }
  const TitleSection = (
    <TitleContainer
      align="center"
      justify="space-between"
      $isHighlighted={isHighlighted}
      $isError={complaint?.level === 'error'}
      $isWarning={complaint?.level === 'warn'}
    >
      <Title>{title}</Title>
      <GhostButton
        isDisabled={!isReadyForMapping}
        buttonSize={ButtonSize.SMALL}
        onClick={onFilter}
      >
        <>
          <SettingsIcon />
          {advancedLabel}
        </>
        {hasActiveFilters && (
          <FilterBadgeDot
            size={BadgeDotSize.SMALL}
            color={StatusType.SUCCESS}
          />
        )}
      </GhostButton>
    </TitleContainer>
  );

  return (
    <ColumnContainer
      $borderRight={lastColumn}
      flexDirection="column"
      gap="none"
    >
      {config.configuredType === TableColumnMappingType.AUTO && (
        <AutoConfiguredContainer>
          <AutoConfig
            justify="space-between"
            align="center"
            onClick={onSelectColumn}
          >
            <span>{config.configuredTitle}</span>
            <EditIcon
              iconSize={IconSize.SMALL}
              deprecated_onClick={onSelectColumn}
            />
          </AutoConfig>
          <Confirm align="center" onClick={onConfirm} gap="xsmall">
            <Icon
              iconName={IconName.CHECK_CIRCLE}
              style={{ color: colors.green800 }}
            />
            <span>{confirmLabel}</span>
          </Confirm>
        </AutoConfiguredContainer>
      )}
      {config.configuredType !== TableColumnMappingType.AUTO && (
        <>
          {config.configuredType === TableColumnMappingType.NONE && (
            <NoneConfigure
              onClick={onSelectColumn}
              data-testid="tabularMapping-column-header"
            >
              <span>{clickToConfigureLabel}</span>
              <EditIcon deprecated_onClick={onSelectColumn} />
            </NoneConfigure>
          )}
          {config.configuredType === TableColumnMappingType.DISABLED && (
            <DisabledConfigure>
              <span>{clickToConfigureLabel}</span>
              <EditIcon />
            </DisabledConfigure>
          )}
          {config.configuredType === TableColumnMappingType.MANUAL &&
            !config.configuredError && (
              <SuccessConfigure onClick={onSelectColumn}>
                <span>{config.configuredTitle}</span>
                <EditIcon
                  iconSize={IconSize.SMALL}
                  deprecated_onClick={onSelectColumn}
                />
              </SuccessConfigure>
            )}
          {config.configuredType === TableColumnMappingType.MANUAL &&
            config.configuredError && (
              <ErrorConfigure onClick={onSelectColumn}>
                <span>{config.configuredTitle}</span>
                <EditIcon
                  iconSize={IconSize.SMALL}
                  deprecated_onClick={onSelectColumn}
                />
              </ErrorConfigure>
            )}
        </>
      )}
      {complaint ? (
        <WithPopover content={complaint.message}>{TitleSection}</WithPopover>
      ) : (
        TitleSection
      )}
    </ColumnContainer>
  );
};

const NoConfig = styled(Box)`
  margin: auto auto;
`;

const AutoConfiguredContainer = styled.div`
  background-color: ${colors.blue50};
  border-top: 3px solid ${colors.blue600};
  border-bottom: 1px solid ${colors.grey300};
`;

const AutoConfig = styled(FlexBox).attrs({ gap: 'medium' })<{
  onClick?: MouseEventHandler<HTMLDivElement>;
}>`
  background-color: ${colors.blue50};
  padding: ${s4} ${s16};
`;

const Confirm = styled(FlexBox)<{
  onClick?: MouseEventHandler<HTMLDivElement>;
}>`
  background-color: ${colors.transparent5};
  padding: ${s8} ${s16};
  cursor: pointer;
`;

const Configure = styled(FlexBox)<{
  onClick?: MouseEventHandler<HTMLDivElement>;
}>`
  cursor: pointer;
  flex-grow: 1;
  padding: ${s16};
`;
Configure.defaultProps = {
  justify: 'space-between',
  align: 'center',
  gap: 'medium',
};

const DisabledConfigure = styled(Configure)`
  border-top: 3px solid ${colors.grey300};
  background-color: ${colors.grey50};
  color: ${colors.grey200};
  border-bottom: 1px solid ${colors.grey300};
`;

const NoneConfigure = styled(Configure)`
  border-top: 3px solid ${colors.grey500};
  background-color: ${colors.grey50};
  color: ${colors.grey950};
  border-bottom: 1px solid ${colors.grey300};
`;

const SuccessConfigure = styled(Configure)`
  background-color: ${colors.blue200};
  border-top: 3px solid ${colors.grey500};
  border-bottom: 1px solid ${colors.grey300};
`;

const ErrorConfigure = styled(Configure)`
  background-color: ${colors.red50};
  border-top: 3px solid ${colors.red200};
  border-bottom: 1px solid ${colors.red200};
`;

const ColumnContainer = styled(FlexBox)<{ $borderRight: boolean }>`
  flex: 1;
  width: 100%;
  height: 100%;
  border-left: 1px solid ${colors.grey300};
  border-right: ${({ $borderRight }) =>
    $borderRight ? `1px solid ${colors.grey300}` : 'unset'};
`;

const TitleContainer = styled(FlexBox).attrs({ gap: 'medium' })<{
  $isHighlighted?: boolean;
  $isError?: boolean;
  $isWarning?: boolean;
}>`
  padding: 0px ${s16};
  height: ${s64};
  white-space: nowrap;
  background-color: ${({ $isHighlighted, $isError, $isWarning }) =>
    $isHighlighted
      ? colors.blue50
      : $isError
        ? colors.red50
        : $isWarning
          ? colors.yellow50
          : 'transparent'};
  border-color: ${({ $isError, $isWarning }) =>
    $isError ? colors.red200 : $isWarning ? colors.yellow500 : 'unset'};
`;

const Title = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FilterBadgeDot = styled(BadgeDot)`
  position: absolute;
  right: -${s8};
  top: 0;
`;
